
import "./index.css";
import './i18n'; // Import i18n configuration
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import Loading from "./components/Loading";
import { useTranslation } from 'react-i18next';
import ProtectedRoute from "./components/ProtectedRoute";
import OnboardingGuard from "./components/OnboardingGuard";
import ErrorBoundary from "./components/ErrorBoundary";
import { DialogHost } from "./components/AppDialog";
import NotFound from "./pages/NotFound";
import { BRAND, crossDomainRedirect, type Brand } from "./brand";

// Sends a public marketing route to its owning brand's domain when viewed on the
// other brand's production domain (no-op on staging/localhost/override).
function BrandGate({ brand, children }: { brand: Brand; children: React.ReactNode }) {
  const target = crossDomainRedirect(brand);
  if (target) { window.location.replace(target); return null; }
  return <>{children}</>;
}

const Login = lazy(() => import("./pages/Login"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminSignup = lazy(() => import("./pages/AdminSignup"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword"));
const Signup = lazy(() => import("./pages/Signup"));
const Msignup = lazy(() => import("./pages/Msignup"));
const Countrysl = lazy(() => import("./pages/Countrysl"));
// Legacy Goal Assessment + IR Form1/Form2 flow removed — superseded by the
// /readiness-journey (CapitalAssessment) onboarding.
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Raise_capital = lazy(() => import("./pages/Raise_capital"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Demo1 = lazy(() => import("./pages/Demo1"));
const Demo2 = lazy(() => import("./pages/Demo2"));
const DemoSchedule = lazy(() => import("./pages/DemoSchedule"));
const RaisingSchedule = lazy(() => import("./pages/RaisingSchedule"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const Profile = lazy(() => import("./pages/Profile"));
const DataRoom = lazy(() => import("./pages/DataRoom"));
const Articles = lazy(() => import("./pages/Articles"));
const IRScore = lazy(() => import("./pages/IRScore"));
const Careers = lazy(() => import("./pages/Careers"));
const Partner = lazy(() => import("./pages/Partner"));
const Sprints = lazy(() => import("./pages/Sprints"));
const ExpertNetwork = lazy(() => import("./pages/ExpertNetwork"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Blog = lazy(() => import("./pages/Blog"));
const ConncctPrivate = lazy(() => import("./pages/ConncctPrivate"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const AdminPartnersHub = lazy(() => import("./pages/AdminPartnersHub"));
const AiTechnology = lazy(() => import("./pages/ConsultingServices"));
const FinancialAdvisory = lazy(() => import("./pages/ConsultingServices").then(m => ({ default: m.FinancialAdvisory })));
const FinancialOverview = lazy(() => import("./pages/ConsultingOverview"));
const AiOverview = lazy(() => import("./pages/ConsultingOverview").then(m => ({ default: m.AiOverview })));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const KPIs = lazy(() => import("./pages/KPIs"));
const Financials = lazy(() => import("./pages/Financials"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminProtectedRoute = lazy(() => import("./components/AdminProtectedRoute"));
const RaisingRequest = lazy(() => import("./pages/RaisingRequest"));
const AdminProfile = lazy(() => import("./pages/AdminProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const SendInvitation = lazy(() => import("./pages/SendInvitation"));
const AdminUserDetail = lazy(() => import("./pages/AdminUserDetail"));
const AdminPartners = lazy(() => import("./pages/AdminPartners"));
const Pricing2 = lazy(() => import("./pages/Pricing2"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const Dm = lazy(() => import("./pages/Dm"));
const Dm0 = lazy(() => import("./pages/Dm0"));
const Dm1 = lazy(() => import("./pages/Dm1"));
const Dm2 = lazy(() => import("./pages/Dm2"));
const Dm3 = lazy(() => import("./pages/Dm3"));
const Dm4 = lazy(() => import("./pages/Dm4"));
const CapitalAssessment = lazy(() => import("./pages/CapitalAssessment"));
const CapitalReadinessAssessment = lazy(() => import("./pages/CapitalReadinessAssessment"));
const FinancialHealthCheck = lazy(() => import("./pages/FinancialHealthCheck"));
const SetupBusinessType = lazy(() => import("./pages/SetupBusinessType"));
const SetupService = lazy(() => import("./pages/SetupService"));
const OperateCompanyInfo = lazy(() => import("./pages/OperateCompanyInfo"));
const OperateCompanyDoing = lazy(() => import("./pages/OperateCompanyDoing"));
const OperatePlan = lazy(() => import("./pages/OperatePlan"));
const OperateInvoice = lazy(() => import("./pages/OperateInvoice"));
const SetupCompanyName = lazy(() => import("./pages/SetupCompanyName"));
const SetupEmirate = lazy(() => import("./pages/SetupEmirate"));
const SetupPlan = lazy(() => import("./pages/SetupPlan"));
const SetupExpedited = lazy(() => import("./pages/SetupExpedited"));
const SetupUpgrade = lazy(() => import("./pages/SetupUpgrade"));
const SetupInvoice = lazy(() => import("./pages/SetupInvoice"));
const Services = lazy(() => import("./pages/Services"));
const ReferAndEarn = lazy(() => import("./pages/ReferAndEarn"));
const BookConsultation = lazy(() => import("./pages/BookConsultation"));
const Notifications = lazy(() => import("./pages/Notifications"));
const NotificationDetail = lazy(() => import("./pages/NotificationDetail"));
const AdminServices = lazy(() => import("./pages/AdminServices"));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const AdminAccounting = lazy(() => import("./pages/AdminAccounting"));
const AdminComplianceCalendar = lazy(() => import("./pages/AdminComplianceCalendar"));
const AdminPricingControl = lazy(() => import("./pages/AdminPricingControl"));
const AdminCorporatePricing = lazy(() => import("./pages/AdminCorporatePricing"));
const AdminConsultingPricing = lazy(() => import("./pages/AdminConsultingPricing"));
const AdminSetupPricing = lazy(() => import("./pages/AdminSetupPricing"));
const AdminReportPricing = lazy(() => import("./pages/AdminReportPricing"));
const AdminCommandCenter = lazy(() => import("./pages/AdminCommandCenter"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminIntake = lazy(() => import("./pages/AdminIntake"));
const AdminContentStudio = lazy(() => import("./pages/AdminContentStudio"));
const AdminReportContent = lazy(() => import("./pages/AdminReportContent"));
const AdminNews = lazy(() => import("./pages/AdminNews"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminVIP = lazy(() => import("./pages/AdminVIP"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const MessageAdmin = lazy(() => import("./pages/MessageAdmin"));
const CapitalRaising = lazy(() => import("./pages/CapitalRaising"));
const CapitalReadiness = lazy(() => import("./pages/CapitalReadiness"));
const Accounting = lazy(() => import("./pages/Accounting"));
const AccountingPricing = lazy(() => import("./pages/AccountingPricing"));
const AccountingDataRoom = lazy(() => import("./pages/AccountingDataRoom"));
const ConsultingPricing = lazy(() => import("./pages/ConsultingPricing"));
const AccountingSignup = lazy(() => import("./pages/AccountingSignup"));
const ConsultingSignup = lazy(() => import("./pages/ConsultingSignup"));
const AccountingLogin = lazy(() => import("./pages/AccountingLogin"));
const ConsultingLogin = lazy(() => import("./pages/ConsultingLogin"));
const AccountingSelect = lazy(() => import("./pages/AccountingSelect"));
const MyActiveServices = lazy(() => import("./pages/MyActiveServices"));
const CorporateServices = lazy(() => import("./pages/CorporateServices"));
const CorporatePricing = lazy(() => import("./pages/CorporatePricing"));
const GrowRaise = lazy(() => import("./pages/GrowRaise"));
const RunComply = lazy(() => import("./pages/RunComply"));

function AppContent() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  useEffect(() => {
    // Set document language and direction
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  // Scroll to the top of the page on every route change (React Router doesn't do this
  // by default, so a new page would otherwise open at the previous scroll position).
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return (
    <ErrorBoundary>
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={BRAND === 'corporate' ? <RunComply /> : <GrowRaise />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin-signup" element={<AdminSignup />} />
        <Route path="/admin/create-owner" element={<AdminSignup />} />
        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/Msignup" element={<Msignup />} />
        <Route path="/email-verification" element={<EmailVerification />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/Countrysl" element={
          <OnboardingGuard requiredStep="country">
            <Countrysl />
          </OnboardingGuard>
        } />
        <Route path="/raisedcap" element={<Raise_capital />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pricing2" element={<Pricing2 />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/partner" element={<Partner />} />
        <Route path="/sprints" element={<BrandGate brand="consulting"><Sprints /></BrandGate>} />
        <Route path="/experts" element={<BrandGate brand="consulting"><ExpertNetwork /></BrandGate>} />
        <Route path="/about" element={<AboutUs />} />
        {/* Blog is brand-aware (posts can target one site or both), so it serves on both domains. */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        {/* Served on BOTH brand domains — the page itself is brand-aware, so no BrandGate. */}
        <Route path="/private" element={<ConncctPrivate />} />
        <Route path="/conncct-private" element={<ConncctPrivate />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        {/* Consulting service pages — brand-gated: these are Grow & Raise offerings. */}
        {/* The overview is the page the nav points at; the full practice-by-practice
            catalogue sits one level down and is linked from it. */}
        <Route path="/services/financial-advisory" element={<BrandGate brand="consulting"><FinancialOverview /></BrandGate>} />
        <Route path="/services/ai-technology" element={<BrandGate brand="consulting"><AiOverview /></BrandGate>} />
        <Route path="/services/financial-advisory/catalogue" element={<BrandGate brand="consulting"><FinancialAdvisory /></BrandGate>} />
        <Route path="/services/ai-technology/catalogue" element={<BrandGate brand="consulting"><AiTechnology /></BrandGate>} />
        <Route path="/admin/partners-hub" element={<AdminProtectedRoute><AdminPartnersHub /></AdminProtectedRoute>} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/demo1" element={<Demo1 />} />
        <Route path="/demo2" element={<Demo2 />} />
        <Route path="/demo/schedule" element={<DemoSchedule />} />
        <Route path="/raising/schedule" element={<RaisingSchedule />} />
        {/* Protected Routes - Require Authentication */}
        {/* /goal, /form, /form2 (legacy Goal Assessment + IR forms) removed —
            the consulting onboarding now runs through /readiness-journey. */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/company-profile" element={
          <ProtectedRoute>
            <CompanyProfile />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/data-room" element={
          <ProtectedRoute>
            <DataRoom />
          </ProtectedRoute>
        } />
        {/* Signed-in reading view of the published articles (same store as /blog). */}
        <Route path="/articles" element={
          <ProtectedRoute>
            <Articles />
          </ProtectedRoute>
        } />
        <Route path="/articles/:slug" element={
          <ProtectedRoute>
            <Articles />
          </ProtectedRoute>
        } />
        {/* Was /insights until it was renamed — keep old links working. */}
        <Route path="/insights" element={<Navigate to="/articles" replace />} />
        <Route path="/insights/:slug" element={<Navigate to="/articles" replace />} />
        <Route path="/ir-score" element={
          <ProtectedRoute>
            <IRScore />
          </ProtectedRoute>
        } />
        <Route path="/kpis" element={
          <ProtectedRoute>
            <KPIs />
          </ProtectedRoute>
        } />
        <Route path="/financials" element={
          <ProtectedRoute>
            <Financials />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminProtectedRoute>
            {BRAND === 'corporate' ? <Navigate to="/admin/accounting" replace /> : <Admin />}
          </AdminProtectedRoute>
        } />
        <Route path="/admin/user/:uid" element={
          <AdminProtectedRoute>
            <AdminUserDetail />
          </AdminProtectedRoute>
        } />
        <Route path="/raising-request" element={<AdminProtectedRoute section="raising"><RaisingRequest /></AdminProtectedRoute>} />
        <Route path="/admin-profile" element={<AdminProtectedRoute><AdminProfile /></AdminProtectedRoute>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/partners" element={
          <AdminProtectedRoute section="partners">
            <AdminPartners />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/partners" element={
          <AdminProtectedRoute section="partners">
            <AdminPartners />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/services" element={
          <AdminProtectedRoute section="services">
            <AdminServices />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/notifications" element={
          <AdminProtectedRoute section="notifications">
            <AdminNotifications />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/accounting" element={
          <AdminProtectedRoute>
            <AdminAccounting />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/compliance-calendar" element={
          <AdminProtectedRoute section="compliance">
            <AdminComplianceCalendar />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/pricing" element={
          <AdminProtectedRoute section="pricing">
            <AdminPricingControl />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/corporate-pricing" element={
          <AdminProtectedRoute section="pricing">
            <AdminCorporatePricing />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/consulting-pricing" element={
          <AdminProtectedRoute section="pricing">
            <AdminConsultingPricing />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/setup-pricing" element={
          <AdminProtectedRoute section="pricing">
            <AdminSetupPricing />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/report-pricing" element={
          <AdminProtectedRoute section="pricing">
            <AdminReportPricing />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/command-center" element={
          <AdminProtectedRoute section="commandCenter">
            <AdminCommandCenter />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/audit-log" element={
          <AdminProtectedRoute section="audit">
            <AdminAuditLog />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/leads" element={
          <AdminProtectedRoute section="leads">
            <AdminLeads />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/intake" element={
          <AdminProtectedRoute section="leads">
            <AdminIntake />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/content-studio" element={
          <AdminProtectedRoute section="content">
            <AdminContentStudio />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/report-content" element={
          <AdminProtectedRoute section="content">
            <AdminReportContent />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/news" element={
          <AdminProtectedRoute section="content">
            <AdminNews />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/blog" element={
          <AdminProtectedRoute section="content">
            <AdminBlog />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/vip" element={
          <AdminProtectedRoute section="team">
            <AdminVIP />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <AdminProtectedRoute section="analytics">
            <AdminAnalytics />
          </AdminProtectedRoute>
        } />
        <Route path="/send-invitation" element={<AdminProtectedRoute section="team"><SendInvitation /></AdminProtectedRoute>} />
        <Route path="/dm" element={<Dm />} />
        <Route path="/dm0" element={<Dm0 />} />
        <Route path="/dm1" element={<Dm1 />} />
        <Route path="/dm2" element={<Dm2 />} />
        <Route path="/dm3" element={<Dm3 />} />
        <Route path="/dm4" element={<Dm4 />} />
        <Route path="/readiness-journey" element={
          <ProtectedRoute>
            <CapitalAssessment />
          </ProtectedRoute>
        } />
        <Route path="/capital-assessment" element={
          <ProtectedRoute>
            <CapitalReadinessAssessment />
          </ProtectedRoute>
        } />
        <Route path="/financial-health-check" element={
          <ProtectedRoute>
            <FinancialHealthCheck />
          </ProtectedRoute>
        } />
        <Route path="/setup-business-type" element={<ProtectedRoute><SetupBusinessType /></ProtectedRoute>} />
        <Route path="/setup-service" element={<ProtectedRoute><AccountingSelect /></ProtectedRoute>} />
        <Route path="/setup-company-name" element={<ProtectedRoute><SetupCompanyName /></ProtectedRoute>} />
        <Route path="/setup-emirate" element={<ProtectedRoute><SetupEmirate /></ProtectedRoute>} />
        <Route path="/setup-plan" element={<ProtectedRoute><SetupPlan /></ProtectedRoute>} />
        <Route path="/setup-expedited" element={<ProtectedRoute><SetupExpedited /></ProtectedRoute>} />
        <Route path="/setup-upgrade" element={<ProtectedRoute><SetupUpgrade /></ProtectedRoute>} />
        <Route path="/setup-invoice" element={<ProtectedRoute><SetupInvoice /></ProtectedRoute>} />
        <Route path="/operate-company-info" element={<ProtectedRoute><OperateCompanyInfo /></ProtectedRoute>} />
        <Route path="/operate-company-doing" element={<ProtectedRoute><OperateCompanyDoing /></ProtectedRoute>} />
        <Route path="/operate-plan" element={<ProtectedRoute><OperatePlan /></ProtectedRoute>} />
        <Route path="/operate-invoice" element={<ProtectedRoute><OperateInvoice /></ProtectedRoute>} />
        <Route path="/services" element={
          <ProtectedRoute>
            <Services />
          </ProtectedRoute>
        } />
        <Route path="/refer-and-earn" element={
          <ProtectedRoute>
            <ReferAndEarn />
          </ProtectedRoute>
        } />
        <Route path="/book-consultation" element={
          <ProtectedRoute>
            <BookConsultation />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        } />
        <Route path="/notifications/:id" element={
          <ProtectedRoute>
            <NotificationDetail />
          </ProtectedRoute>
        } />
        <Route path="/message-admin" element={
          <ProtectedRoute>
            <MessageAdmin />
          </ProtectedRoute>
        } />
        <Route path="/accounting/select" element={<ProtectedRoute><AccountingSelect /></ProtectedRoute>} />
        <Route path="/accounting/signup" element={<AccountingSignup />} />
        <Route path="/accounting/login" element={<AccountingLogin />} />
        <Route path="/consulting/signup" element={<ConsultingSignup />} />
        <Route path="/consulting/login" element={<ConsultingLogin />} />
        <Route path="/consulting" element={<BrandGate brand="consulting"><CapitalRaising /></BrandGate>} />
        <Route path="/capital-raising" element={<BrandGate brand="consulting"><CapitalReadiness /></BrandGate>} />
        <Route path="/accounting" element={<BrandGate brand="corporate"><Accounting /></BrandGate>} />
        <Route path="/accounting/pricing" element={<BrandGate brand="corporate"><AccountingPricing /></BrandGate>} />
        <Route path="/accounting/data-room" element={
          <ProtectedRoute>
            <AccountingDataRoom />
          </ProtectedRoute>
        } />
        <Route path="/capital-raising/pricing" element={<BrandGate brand="consulting"><ConsultingPricing /></BrandGate>} />
        <Route path="/corporate" element={<BrandGate brand="corporate"><CorporateServices /></BrandGate>} />
        <Route path="/corporate/pricing" element={<BrandGate brand="corporate"><CorporatePricing /></BrandGate>} />
        <Route path="/my-services" element={<ProtectedRoute><MyActiveServices /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
      <DialogHost />
    </BrowserRouter>
  );
}

export default App;
